import { useContext, useEffect, useMemo, useState } from 'react';
import { VirtualTable } from '@f_general/stackTable/VirtualTable';
import { useTranslation } from 'react-i18next';
import stls from '@f_qwep/styles/component/tables/searchResultTable/ColumnStyle.module.sass';
import { ThemeContext } from '@f_context/Theme_context';
import { ColumnDef } from '@tanstack/react-table';
import { useWindowSize } from '@f_hooks/index';
import { CellVendor } from './columnVendor';
import { CellTitle } from './columnTitle';
import { CellPrice } from './columnPrice';
import { CellAvailability } from './columnAvailability';
import { CellQuantity } from './columnQuantity';
import { CellTime } from './columnTime';
import { CellArticle } from './columnArticle';
import { CellWarehouse } from './columnWarehouse';
import { SearchContext } from '@f_qwep/context/Search_context';
import { FilterReset } from '../../searchResult/FilterReset';
import _ from 'lodash';
import cn from 'classnames';
import { QwepContext } from '@f_qwep/context/Qwep_context';
import { CellMobile } from './mobileColumns';
import { useMaskCrossTransformItems } from '@f_qwep/components/filters/hooks/useMaskCrossTransformItems';
import { useFilteredResults } from '@f_qwep/components/filters/hooks/useFilteredResults';

export function SearchResultTable({ items, filter, filtersReducer, setItemsTable }: any) {
    const { t } = useTranslation();
    const { colors } = useContext(ThemeContext);
    const { width, height } = useWindowSize();
    const [mobile, setMobile] = useState(false);

    const { tabId, arrSearchResults } = useContext(SearchContext);
    const { priceMarginIsActive, priceMarginState, setOpenExportModal, setExportData, setTypeExport } =
        useContext(QwepContext);

    const searchItem = arrSearchResults.filter((el: any) => el.tabId == tabId)[0];

    useEffect(() => {
        setMobile(width < 700);
    }, [width]);

    //Преобразование при включенных mask cross
    items = useMaskCrossTransformItems(items);

    const columns = useMemo<ColumnDef<any>[]>(
        () =>
            mobile
                ? [
                      {
                          id: 'title',
                          accessorKey: 'title',
                          header: `${t('qwep.settings.suppliers.supplier')}`,
                          cell: (info) => <CellMobile info={info} />,
                          footer: (props) => props.column.id,
                          size: 1,
                      },
                  ]
                : [
                      {
                          id: 'Поставщик',
                          accessorKey: 'vendorTitle',
                          header: `${t('qwep.search.vendor')}`,
                          cell: (info) => <CellVendor info={info} />,
                          footer: (props) => props.column.id,
                          size: 60,
                      },
                      {
                          id: 'Производитель',
                          accessorKey: 'brand',
                          header: `${t('qwep.search.brand')}`,
                          cell: (info) => (
                              <div style={{ color: colors.alpha }} className={cn(stls.cellBrand)}>
                                  {info.row.original.brand}
                              </div>
                          ),
                          footer: (props) => props.column.id,
                          size: 60,
                      },
                      {
                          id: 'Артикул',
                          accessorKey: 'article',
                          header: `${t('qwep.search.article')}`,
                          cell: (info) => <CellArticle info={info} setItemsTable={setItemsTable} />,
                          footer: (props) => props.column.id,
                          size: 60,
                      },
                      {
                          id: 'НазваниеТаблРезульт',
                          accessorKey: 'title',
                          header: `${t('qwep.search.partname')}`,
                          cell: (info) => <CellTitle info={info} />,
                          footer: (props) => props.column.id,
                          minSize: 180,
                      },
                      {
                          id: 'Цена',
                          accessorKey: 'price',
                          header: `${t('qwep.search.price')}`,
                          cell: (info) => <CellPrice info={info} />,
                          footer: (props) => props.column.id,
                          minSize: 30,
                          size: 50,
                          sortingFn: (a: any, b: any) =>
                              priceMarginIsActive
                                  ? +b.original.priceWithMargin - +a.original.priceWithMargin
                                  : b.original.price.value - a.original.price.value,
                      },
                      {
                          id: 'Срок',
                          accessorKey: 'time',
                          header: `${t('qwep.search.deliveryTime')}`,
                          cell: (info) => <CellTime info={info} />,
                          footer: (props) => props.column.id,
                          sortingFn: (a: any, b: any) => {
                              const dateA =
                                  new Date(b.original.deliveryEnd).getFullYear() == 2001
                                      ? new Date(b.original.deliveryStart)
                                      : new Date(b.original.deliveryEnd);
                              const dateB =
                                  new Date(a.original.deliveryEnd).getFullYear() == 2001
                                      ? new Date(a.original.deliveryStart)
                                      : new Date(a.original.deliveryEnd);
                              return +dateA - +dateB;
                          },
                          size: 70,
                      },
                      {
                          id: 'Наличие',
                          accessorKey: 'quantity.formatted',
                          header: `${t('qwep.search.availability')}`,
                          cell: (info) => <CellAvailability info={info} />,
                          footer: (props) => props.column.id,
                          size: 50,
                      },
                      {
                          id: 'Склад',
                          accessorKey: 'warehouse',
                          header: `${t('qwep.search.warehouse')}`,
                          cell: (info) => <CellWarehouse info={info} />,
                          footer: (props) => props.column.id,
                          size: 50,
                      },
                      {
                          id: 'Склад Поставщика',
                          accessorKey: 'origWarehouse',
                          header: `${t('qwep.search.vendorsWarehouse')}`,
                          cell: (info) => <div className={stls.cellWarehouse}>{info.row.original.origWarehouse}</div>,
                          footer: (props) => props.column.id,
                          size: 10,
                          minSize: 10,
                      },
                      {
                          id: 'Корзина',
                          accessorKey: `${t('qwep.search.basket')}`,
                          header: '',
                          cell: (info) => <CellQuantity info={info} />,
                          footer: (props) => props.column.id,
                          size: 80,
                          minSize: 80,
                      },
                  ],
        [items, mobile],
    );

    const data = useFilteredResults(items, filter, priceMarginIsActive, priceMarginState, searchItem);

    const sortResult = (data: any) => {
        const priceSorter = (data: any) => +data.price.value;
        const priceWithMarginSorter = (data: any) => +data.priceWithMargin;

        return _.orderBy(data, priceMarginIsActive ? priceWithMarginSorter : priceSorter);
    };

    const exportExcelHandler = () => {
        setTypeExport('searchResult');
        setExportData({ filter: sortResult(data), all: searchItem.items });
        setOpenExportModal(true);
    };

    const tableStyles = {
        head: {
            // background: colors.beta,
            container: {
                height: '50px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: mobile ? 'center' : null,
            },
            base: true,
            title: {
                fontSize: '14px',
            },
            inputFilter: {
                borderRadius: '7px',
            },
        },
        body: {
            rowRadius: true,
            row: {
                background: colors.psi,
                height: mobile ? '190px' : '38px',
            },
            marginRow: {
                margin: '1px 0',
            },
            cellRadius: true,
        },
    };

    return (
        <VirtualTable
            searchId={tabId}
            data={sortResult(data)}
            columnsTable={columns}
            topFilterPanel={width > 700}
            showColumnSearchAction={width > 700}
            heightTable={height - 97}
            configHeader={{
                chipFilters: <FilterReset filter={filter} filtersReducer={filtersReducer} />,
                fullScrean: true,
                exportExcel: true,
                resize: true,
                grouping: false,
                columnFilter: true,
                sorting: width > 700,
                columnSearch: true,
                dragging: true,
            }}
            styles={tableStyles}
            overscan={2}
            exportExcelHandler={exportExcelHandler}
            estimateSize={mobile ? 191 : 39}
        />
    );
}
